import {
  MODULE_FAMILY_CORRIDOR,
  getPickableElements,
  type MapEditorCatalogElement,
} from './mapEditorCatalog';

// The kit as an author meets it: a handful of root types, each holding the variants that actually
// differ. The picker asks two questions — which kind of thing, then which one of those — and the
// second is asked from a carousel under a picture rather than from a tree of facets.
//
// This replaced a facet tree (category → family → direction → size → module) that the kit outgrew.
// Two things broke it, and both are still true of the shipped catalog, so neither may be designed
// away again:
//
//  - **Facets no longer identify a corridor.** Four corridor families ship — plain, Screen, Lit and
//    Lit II — each covering the same six direction × size combinations. All 24 therefore ended in a
//    fall-through leaf holding four indistinguishable modules, and the popup written for that rare
//    case became the normal path.
//  - **Direction is not a question about a room.** `room-rotunda-left` / `-right` are `family: room`
//    with a direction, so "rooms are always straight" was never true; and asking which way a
//    *gallery* should turn is a question about nothing. Direction is a property of a variant here,
//    never a filter.
export type MapEditorRootKind = 'room' | 'corridor' | 'entrance';

// One pickable module, as a choice under its root.
export interface MapEditorVariant {
  element: MapEditorCatalogElement;
  // What tells this one apart from its siblings, already assembled from whichever of size and
  // direction actually varies within the root. Localized by the caller through `sizeKey` /
  // `directionKey`; `fallbackLabel` is the module's own name, for a root whose variants nothing
  // distinguishes — a single-variant root, most often.
  sizeKey: string;
  directionKey: string;
  fallbackLabel: string;
}

export interface MapEditorRootType {
  // Stable across renders and unique within the kit, because it is what the grouping keyed on. It
  // joins the two halves on a NUL, which is the separator this codebase already uses for a composite
  // key (see brandingEntitiesCsv): no authored string can contain one, so no pair of category and
  // name can collide with another pair by running together.
  //
  // Not for the DOM. A NUL in an attribute is silently dropped by some serialisers and unreadable in
  // all of them — that is what `slug` is for.
  id: string;
  // The same identity, safe to put in a `data-testid` or a DOM id: lower case, non-word runs
  // collapsed to a hyphen.
  slug: string;
  // The kit's own words with the variant tail removed. Authored text, shown as authored — the kit
  // decides whether its rooms are Galleries or Rotundas, and translating it here would mean
  // shipping a hub release to rename a folder in the catalog.
  name: string;
  category: string;
  kind: MapEditorRootKind;
  // Authored prose about the root, when the kit ships any. Empty is the normal case in the shipped
  // kit, and the variant count stands in for it.
  description: string;
  variants: MapEditorVariant[];
}

// The words the kit uses for the two axes a variant varies on. Stripping them off a display name is
// what leaves the root's own name behind: `Corridor Short Right` → `Corridor`, `Main Lobby Small` →
// `Main Lobby`, `Lit Corridor II Long Left` → `Lit Corridor II`.
//
// A word list rather than something cleverer, and it fails in the safe direction. A build that names
// its variants with words not in here simply gets more roots — one per module, each with a single
// variant — so the picker asks one question instead of two and every module stays pickable. The
// opposite trade (guessing which tokens are variant words) would silently merge two different rooms
// under one card and offer the wrong geometry.
const VARIANT_WORDS = [
  'small',
  'medium',
  'large',
  'short',
  'long',
  'left',
  'right',
  'straight',
  'rounded',
];

/**
 * The root's own name: the module's display name with the variant words taken out.
 *
 * @param displayName - The module's name as the kit authored it.
 * @returns The remaining words, in order, or the whole name when nothing but variant words is left.
 */
const rootNameOf = (displayName: string): string => {
  const kept = displayName
    .split(/\s+/)
    .filter((word) => word && !VARIANT_WORDS.includes(word.toLowerCase()));

  // A module called nothing but its size — `Small` on its own — keeps its name rather than joining a
  // nameless root with every other such module in its category.
  return kept.length ? kept.join(' ') : displayName;
};

/**
 * Which tab a root belongs under.
 *
 * Read off the role first: a purpose-built shell hangs its geometry behind its own exit and cannot
 * sit mid-chain, so it is an entrance whatever family it claims. Everything else follows the family,
 * and anything this hub cannot name is a room — the same default the catalog parse applies.
 *
 * @param element - Any module of the root; they agree on both fields by construction.
 * @returns The kind.
 */
const rootKindOf = (element: MapEditorCatalogElement): MapEditorRootKind => {
  if (element.role === 'start') {
    return 'entrance';
  }

  return element.family === MODULE_FAMILY_CORRIDOR ? 'corridor' : 'room';
};

/**
 * Groups the kit into the root types an author picks between, each with its own variants.
 *
 * Keyed on category *and* the stripped name, not on either alone. Category alone is not enough and
 * the shipped catalog is the proof: `Gallery` holds both the plain galleries (`room-1a-*`) and the
 * Screen ones (`room-1b-*`), which share family, direction, size and category and differ only in
 * their name — the category tail they carried in the v3 export (`Screen Gallery`) was lost by v5.
 * The stripped name alone is not enough either, since two categories may legitimately name a room
 * the same thing.
 *
 * Caps are filtered out by getPickableElements, and the purpose-built lobbies are *not* — they are
 * offered here under their own tab, because any room may be an entrance and the shells are the rooms
 * made for it. Nothing in this list may be appended mid-chain by the caller: an entrance is chosen
 * for the entrance row, and the picker knows which row it was opened for.
 *
 * @param elements - The resolved catalog.
 * @returns Root types in catalog order, each carrying its variants in catalog order.
 */
export const buildRootTypes = (
  elements: MapEditorCatalogElement[],
): MapEditorRootType[] => {
  const byKey = new Map<string, MapEditorCatalogElement[]>();

  // The lobbies come back in, on top of the pickable modules: getPickableElements drops both anchor
  // roles, and only the cap has no business being offered.
  const offerable = [
    ...getPickableElements(elements),
    ...elements.filter((element) => element.role === 'start'),
  ];

  offerable.forEach((element) => {
    const key = `${element.category}\u0000${rootNameOf(element.displayName)}`;
    const bucket = byKey.get(key);
    if (bucket) {
      bucket.push(element);
      return;
    }
    byKey.set(key, [element]);
  });

  return Array.from(byKey.entries()).map(([key, bucket]) => {
    const [first] = bucket;
    const sizes = new Set(bucket.map((element) => element.size));
    const directions = new Set(bucket.map((element) => element.direction));

    return {
      id: key,
      slug: key
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''),
      name: rootNameOf(first.displayName),
      category: first.category,
      kind: rootKindOf(first),
      // The first authored one wins. The variants of a root are the same room at different lengths,
      // so a sentence about one of them is a sentence about the root.
      description:
        bucket.find((element) => element.description)?.description ?? '',
      variants: bucket.map((element) => ({
        element,
        // Named only where it distinguishes. A gallery that comes in three sizes is asked about by
        // size; a rotunda that comes in three directions is asked about by direction; a corridor is
        // asked about by both. Offering an axis that has one value is a control that decides nothing.
        sizeKey: sizes.size > 1 ? element.size : '',
        directionKey: directions.size > 1 ? element.direction : '',
        fallbackLabel: element.displayName,
      })),
    };
  });
};

/**
 * The root a module belongs to.
 *
 * Used to seed the picker from what is already standing: opening **Change** on a corridor should land
 * on that corridor's own card with its own variant selected, rather than at the top of the kit.
 *
 * @param roots - The grouped kit.
 * @param elementId - The module to locate.
 * @returns Its root, or undefined for an id this kit does not offer — a cap, or one the catalog has
 * stopped shipping.
 */
export const findRootFor = (
  roots: MapEditorRootType[],
  elementId: string,
): MapEditorRootType | undefined =>
  roots.find((root) =>
    root.variants.some((variant) => variant.element.id === elementId),
  );

/**
 * The roots matching a search box.
 *
 * Searches across every tab rather than within the active one, because that is what a search box is
 * for: an author who types "lit" is looking for the Lit corridors and should not have to know they
 * are filed under Corridors first. The caller shows the result as one list.
 *
 * Matches the root's name, its category and its description — the three pieces of authored text a
 * root has. Not the variant names, which are the same words the size labels already carry and would
 * make every query for "long" return the whole kit.
 *
 * @param roots - The grouped kit.
 * @param query - What the author typed. Blank returns nothing matched, not everything, so the caller
 * decides what an empty box means.
 * @returns The matching roots, in the order given.
 */
export const searchRootTypes = (
  roots: MapEditorRootType[],
  query: string,
): MapEditorRootType[] => {
  const wanted = query.trim().toLowerCase();
  if (!wanted) {
    return [];
  }

  return roots.filter((root) =>
    [root.name, root.category, root.description].some((text) =>
      text.toLowerCase().includes(wanted),
    ),
  );
};

// What a module's spots are, for the one line that says what is in it. Kept apart from the count
// because the two absences differ: `unknown` is a level the build could not read, and `bare` is a
// module that really holds nothing — one shipped module is genuinely bare, and telling an author it
// is unmeasured would be a different and wrong answer.
export type MapEditorSpotKind = 'unknown' | 'bare' | 'pictures' | 'screens';

export interface MapEditorSpotCount {
  kind: MapEditorSpotKind;
  total: number;
}

/**
 * What is on this module's walls, as the picker says it.
 *
 * @param element - The module.
 * @returns The kind and the count. `screens` only for a module whose spots are all video, since a
 * mixed module is described by the commoner word rather than by the rarer one.
 */
export const spotCountOf = (
  element: MapEditorCatalogElement,
): MapEditorSpotCount => {
  const { spots } = element;
  if (!spots) {
    return { kind: 'unknown', total: 0 };
  }

  if (!spots.total) {
    return { kind: 'bare', total: 0 };
  }

  const byType = spots.byType ?? {};
  const videos = byType.VIDEO ?? 0;

  return {
    kind: videos === spots.total ? 'screens' : 'pictures',
    total: spots.total,
  };
};
